import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { api, formatError } from "@/lib/api";
import GoogleSignInButton from "@/components/app/GoogleSignInButton";
import { FreeNotice } from "@/components/app/PlanCard";

const REGIONS = ["Waikato", "Auckland", "Bay of Plenty", "Wellington", "Canterbury", "Otago", "Northland", "Manawatu", "Hawke's Bay", "Taranaki", "Nelson", "Southland"];

export default function Register() {
  const { register } = useAuth();
  const [form, setForm] = useState({ email: "", password: "", store_name: "", region: "Waikato", phone: "", address: "" });
  const [loading, setLoading] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [cfg, setCfg] = useState(null);
  useEffect(() => { api.get("/config").then(r => setCfg(r.data)).catch(() => {}); }, []);
  const navigate = useNavigate();

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!agreed) { toast.error("Please agree to the Terms and Privacy Policy first"); return; }
    setLoading(true);
    try {
      await register(form);
      toast.success("Store registered! Your account is awaiting admin approval before you can publish.", { duration: 6000 });
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-lg mx-auto px-4 py-14">
      <Card className="border-slate-200 shadow-lg">
        <CardHeader>
          <CardTitle className="font-display text-2xl">Register your store</CardTitle>
          <CardDescription>Join the Fiver Deal: NZ$5/month for 28 adverts, price locked for 12 months, cancel any time. Extra adverts $2 each.</CardDescription>
        </CardHeader>
        <CardContent>
          {cfg?.in_free_period && <div className="mb-4 space-y-1"><FreeNotice freeUntil={cfg.free_until} /><p className="text-xs text-fuchsia-800 font-semibold" data-testid="founding-seller-note">Sign up during the free period and get a Founding Seller badge on your store and adverts.</p></div>}
          <GoogleSignInButton label="Sign up with Google" />
          <div className="my-4 flex items-center gap-2 text-xs text-slate-400"><div className="h-px bg-slate-200 flex-1"/>or fill in the details<div className="h-px bg-slate-200 flex-1"/></div>
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-3 mb-4 flex items-center gap-3" data-testid="mobile-qr">
            <img alt="Scan to register on your phone" src={`https://api.qrserver.com/v1/create-qr-code/?size=140x140&margin=2&data=${encodeURIComponent(window.location.origin + "/register")}`} className="w-16 h-16 rounded bg-white border border-emerald-200" />
            <div className="text-xs text-emerald-900 leading-snug">
              <div className="font-display font-bold text-slate-900 mb-0.5">Register on your phone</div>
              Scan this QR with your phone camera to open the sign-up form on mobile — snap your first markdown right there.
            </div>
          </div>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label>Store name</Label>
              <Input data-testid="reg-store-name" value={form.store_name} onChange={upd("store_name")} required placeholder="e.g. Hamilton Fresh Grocers" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Region</Label>
                <Select value={form.region} onValueChange={(v)=>setForm({...form, region: v})}>
                  <SelectTrigger data-testid="reg-region"><SelectValue /></SelectTrigger>
                  <SelectContent>{REGIONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Phone (optional)</Label>
                <Input data-testid="reg-phone" value={form.phone} onChange={upd("phone")} placeholder="+64 …" />
              </div>
            </div>
            <div>
              <Label>Address (optional)</Label>
              <Input data-testid="reg-address" value={form.address} onChange={upd("address")} placeholder="Shop street address" />
            </div>
            <div>
              <Label>Email</Label>
              <Input data-testid="reg-email" type="email" value={form.email} onChange={upd("email")} required autoComplete="email" />
            </div>
            <div>
              <Label>Password (min 6)</Label>
              <Input data-testid="reg-password" type="password" minLength={6} value={form.password} onChange={upd("password")} required autoComplete="new-password" />
            </div>
            <Button type="submit" disabled={loading || !agreed} className="w-full bg-emerald-800 hover:bg-emerald-900 h-11" data-testid="reg-submit">
              {loading ? "Creating…" : "Create store account"}
            </Button>
            <label className="flex items-start gap-2 text-xs text-slate-600 cursor-pointer" data-testid="reg-agree-wrap">
              <input type="checkbox" checked={agreed} onChange={(e)=>setAgreed(e.target.checked)} className="mt-0.5" data-testid="reg-agree" />
              <span>I agree to the <a href="/terms" target="_blank" rel="noreferrer" className="text-emerald-800 underline underline-offset-2">Terms of Service</a> and <a href="/privacy" target="_blank" rel="noreferrer" className="text-emerald-800 underline underline-offset-2">Privacy Policy</a>.</span>
            </label>
            <p className="text-sm text-slate-500 text-center">Already have one? <Link to="/login" className="text-emerald-800 font-semibold underline underline-offset-4">Log in</Link></p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
